/*
 * Copyright (c) 2017 Payoneer Germany GmbH. All rights reserved.
 */

/*
 * Ajax integration
 * Main file that contains all the logic
 */
var $ = require('jquery');
var listHandler = require('./list/list-handler');
var { getConfig } = require('./services/config.service');
var loading = require('./services/loading.service');
var applyCustomCss = require('./services/custom-css.service');
var rootScope = require('./services/root-scope.service');
var getListDetails = require('./services/list-details.service');
var polyfills = require('./services/polyfills.service').polyfills;
var sentry = require('./services/sentry.service');
var jqueryBinding = require('./jquery-binding');
var redirectHandler = require('./provider-redirect-handler').providerRedirectHandler;

sentry();
polyfills();

function addSummaryClass(mainContainer, rootScope) {
    rootScope.isSummaryPage() && mainContainer.addClass('op-payment-widget-summary');
}

/**
 * @callback abortFunctionCB
 * @param {Object} [opgResponse] More info about OPG responses https://developer.payoneer.com/orchestration/index.html
 */

/**
 * @callback proceedFunctionCB
 * @param {Object} [opgResponse] More info about OPG responses https://developer.payoneer.com/orchestration/index.html
 */

/**
 * @callback doOperationCB
 * @param {{ method: string, url: string }} data contains operation info. for instance method (POST, GET etc) and url
 * @param {function(): void} callback - Callback function after operation
 */

/**
 * @callback onResultCB
 * @param {Object} [opgResponse] More info about OPG responses https://developer.payoneer.com/orchestration/index.html
 */

/**
 * @typedef {Object} InputConfigProperties
 * @property {string} payButton - The ID of the HTML element of the submit button defined by merchant.
 * @property {string} [baseUrl] - The URL of the OPG service that will handle all the AJAX calls. It depends on the OPG version and environment you are using.
 * @property {string} [listId] - The ID of the LIST object as returned by the LIST Response. If not provided in the JavaScript call, the library will look for a GET query parameter called `listId` and use its value if found.
 * @property {string} [listUrl] - The URL to the generated LIST object as returned by the LIST response. If not provided in the JavaScript call, the library will look for a GET query parameter calles listUrl and use its value if found.
 * @property {string} [widgetCssUrl] - If your application stores CSS files in a different directory level than your checkout page, use this parameter to reference the location of `widget*.css` or `widget-card-view*.css`. payButton - The ID of the HTML element of the submit button defined by merchant.
 * @property {string} [payButtonContainer] - The ID of the HTML element that contains the submit button. Used with methods that require proprietary provider buttons, which will then replace the standard payment button dynamically.
 * @property {string} [templateName="pp3"] - pp3 is the default template
 * @property {boolean} [cardView] - Default: `false`. When set to `true` puts each payment method inside cards. In practice this means that the payment page will render as if in mobile mode, even on desktop clients.
 * @property {boolean} [fullPageLoading] - Default: `true`. When set to `true`, the loading spinner rendered by the widget covers the entire containing page, typically the page where the widget is loading. This means the entire contents are dimmed and inactive. When set to false, the widget renders a spinner only in the container assigned to it. This mode can be used to avoid conflicts with a loading spinner rendered by the shop itself.
 * @property {boolean} [smartSwitch] - Default: `true`. Enables the "Smart Network Switch" feature for cards. "Smart Network Switch" automatically detects the credit card scheme by its card number while typing. It is meaningless for payment methods other than cards.
 * @property {boolean} [summaryPage] - Default: `false`. Used to load alternative payment methods in the summary page. Some methods such as PayPal Smart Buttons or Apple Pay will trigger a payment once their submit button is pressed, so if you want to use a summary page they must be initialized only there.
 * @property {boolean} [liveValidation] - Enable or disable live validation
 * @property {boolean} [suppressIFrameDefault] - Enable or disable
 * @property {boolean} [useAnimations] - Enable animations
 * @property {boolean} [combineRegistrationOptions] - Combine registration options. Default `false`
 * @property {number} [validationDelay]- Number of milliseconds to delay validation
 * @property {number} [animationSpeed] - Speed of animations
 * @property {Object} [summaryPageHandler] - If the AJAX library is initialized in Summary Page mode (see `summaryPage` attribute) this object will contain `doOperation` and/or `onResult` callbacks implemented by you.
 * @property {Object} [listDetails] - Pass the response of List request.
 * @property {abortFunctionCB} [abortFunction] - Custom function to deal with `ABORT` interaction code of
 *  the response object. If set, it will replace the redirect defined in `callback.cancelUrl`.
 * - `abortFunction` has single parameter which contains `opgResponse`
 * @property {proceedFunctionCB} [proceedFunction] - Custom function to deal with `PROCEED` interaction code of the response
 * object. If set, it will replace the redirect defined in `callback.returnUrl` and `callback.summaryUrl`.
 * Note that this function replaces two possible redirection (return on success and summary page), so you have to act on
 * the OPG response object to construct a proper replacement for a success or summary.
 * - `proceedFunction` has single parameter which contains `opgResponse`
 * - More info about `opgResponse` https://developer.payoneer.com/orchestration/index.html
 * @property {function(): number} [iFrameScaleFunction] - For selective native integrations using custom styling via external
 * CSS file, it might be needed to also adapt the container iFrames height to properly fit customized input fields forpayment
 * methods, saved accounts or preset accounts.
 * - `iFrameScaleFunction` has no parameter and it must return a `number`
 * @property {doOperationCB} [doOperation] - If the AJAX library is initialized in Summary Page mode (see `summaryPage`
 * attribute) this callback function can be implemented to execute any operation once the user press the submit button.
 * This function must be declared within a `summaryPageHandler` container object.
 * - `doOperation` has single parameter which contains `opgResponse`
 * @property {onResultCB} [onResult] - If the AJAX library is initialized in Summary Page mode (see `summaryPage` attribute)
 * this callback function can be implemented to handle responses This function must be declared within a `summaryPageHandler`
 * container object. from operations executed after the user presses the submit button
 * - `onResult` has single parameter which contains `opgResponse`
 * @property {boolean} [providerRedirectInNewWindow] - Redirect third party PSP in new tab.
 * @property {string} [providerRedirectWindowSpecs] - `Window.open` specs/features params
 */

/**
 * Initializes the plugin.
 *
 * @param {string} elementId - Element ID
 * @param {InputConfigProperties} inputConfig - {@link InputConfigProperties}
 *
 * @see https://orchestrationdocs.payoneer.com/docs/web-sdk
 */
function checkoutList(elementId, inputConfig) {
    // Get the configuration from query string and choose the right one default -
    // provided in installation - querystring
    var config = getConfig(inputConfig);
    var mainContainer = $('<div class="op-payment-widget-main-container"></div>');
    var listContainer = $('<div class="op-payment-widget-container"></div>');
    mainContainer.append(listContainer);
    $('#' + elementId).append(mainContainer);

    var oldIsEventBound;
    var boundSubmitButton = [];
    if (window.glob && window.glob.isEventBound) {
        oldIsEventBound = window.glob.isEventBound;
    }
    if (window.glob && window.glob.boundSubmitButton) {
        boundSubmitButton = window.glob.boundSubmitButton;
    }
    window.glob = {
        isEventBound: oldIsEventBound,
        boundSubmitButton: boundSubmitButton,
        packageVersion: (process && process.env && process.env.PKGRELEASE_VERSION) || 'localhost',
    };

    // Apply custom css if provided in query string
    applyCustomCss(config.cssUrl);
    // make a REST call to get list of available payment methods and registered The
    // response contains also links for languages
    config.mainContainer = mainContainer;
    config.listContainer = listContainer;
    config.listHandler = listHandler; // put the listHandler in glob to access it without injecting it on re-init
    rootScope.saveConfig(config);
    rootScope.setSubmitButton($('#' + rootScope.getPayButton()));

    // Show a loading
    loading.showFullPageLoading();

    if (rootScope.isCardView()) {
        $('.op-payment-widget-container').addClass('card-view');
        $('#' + rootScope.getPayButton()).addClass('card-view');
        $('#' + rootScope.getPayButtonContainer()).addClass('card-view');
    }

    getListDetails();
    addSummaryClass(mainContainer, rootScope);
}

/**
 * Third party redirect handler which should be available in the
 * return and cancel pages mentioned in list request.
 *
 * @param {function(params?: Object): void} noParentTabCallback - Callback function to execute if the parent tab/window is closed
 *
 * @see https://orchestrationdocs.payoneer.com/docs/integrate-redirect-networks
 * @see https://orchestrationdocs.payoneer.com/docs/create-a-summary-page#code-samples
 */
function providerRedirectHandler(noParentTabCallback) {
    return redirectHandler(noParentTabCallback);
}

jqueryBinding(checkoutList);

export default checkoutList;
export { providerRedirectHandler, checkoutList };
